/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useCallback, useEffect, useMemo } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { useInvoice } from "../lib/react-query/useInvoice";
import LoadingIndicator from "../components/LoadingIndicator";
import SkeletonText from "../components/skeleton/SkeletonText";
import InvoiceCard from "../components/InvoiceCard";
import Container from "../components/Container";
import NavigationHeader from "../components/NavigationHeader";
import { usePaymentProcessors } from "../lib/react-query/usePaymentProcessors";
import PaymentProcessorsList from "../components/booking/PaymentProcessorsList";
import PaymentProcessorItem from "../components/booking/PaymentProcessorItem";
import styled from "styled-components";
import Button from "../components/ui/Button";
import { useInitPayment } from "../lib/react-query/useInitPayment";
import T from "../components/ui/Translation";
import useWidgetCallbackParams from "../hooks/useWidgetCallbackParams";
import {
  buildCallbackUrl,
  buildRelayUrl,
  buildRelayOrigin,
  isWidgetFramed,
} from "../hooks/checkout/buildCallbackUrl";
import usePaymentPopup from "../hooks/checkout/usePaymentPopup";
import useNavigateToPaymentStatus from "../hooks/checkout/useNavigateToPaymentStatus";
import { useConfig } from "../lib/react-query/useConfig";
import RecurringChargesBlock from "../components/membership/RecurringChargesBlock";
import { usePaymentSelection } from "../hooks/checkout/usePaymentSelection";
import SavePaymentMethodOption from "../components/payment/SavePaymentMethodOption";
import SavedPaymentMethodsList from "../components/payment/SavedPaymentMethodsList";
import { useRecurringInvoicePayment } from "../hooks/checkout/useRecurringInvoicePayment";
import Stack from "../components/ui/Stack";
import { isSafeRedirectUrl } from "../utils/sanitize";
import { useToast } from "../components/ui/Toast";
import { extractErrorMessage } from "../utils/api/apiErrors";
import { INVOICE_STATUS, ROUTES, TOAST_ERROR_MESSAGE } from "../constants/constants";
import { getInvoiceStatusRoute } from "../utils/helpers/invoice";
import { useActiveFlow } from "../hooks/useActiveFlow";

const ProceedButton = styled((props) => <Button {...props} />)`
  margin-top: 2rem;
`;

const StyledPaymentProcessorsList = styled((props) => <PaymentProcessorsList {...props} />)`
  margin-top: 2rem;
`;

const StyledSavedPaymentMethodsList = styled(SavedPaymentMethodsList)`
  margin-top: 2rem;
`;

const SaveOptionWrap = styled.div`
  margin-top: 1rem;
`;

const InvoicePay = () => {
  const { payLaterInvoiceId, clearParams } = useWidgetCallbackParams();
  const params = useParams();
  const backTarget = useLocation().state?.from || -1;
  const id = payLaterInvoiceId || params?.id || null;

  const navigate = useNavigate();
  const activeFlow = useActiveFlow();
  const { isLoading, data: invoice } = useInvoice(id);
  const { isLoading: processorsLoading, data: paymentProcessors } = usePaymentProcessors();
  const { data: config } = useConfig();
  const { toast } = useToast();

  const recurringPayment = useRecurringInvoicePayment(invoice, {
    canOptIn: !invoice?.require_recurring_payment_method,
  });

  const payment = usePaymentSelection(invoice, paymentProcessors, {
    disabledPayLater: invoice?.rest_amount > 0 && invoice?.deposit_is_client_can_pay_later === 0,
    hideDelay: invoice?.payment_processor === "delay" || invoice?.status === INVOICE_STATUS.PAID,
    recurringOnly: recurringPayment.isRecurring,
    recurringParams: recurringPayment.recurringParams,
  });
  const { selectedProcessor, filteredPaymentProcessors } = payment;

  // Popup relay flow - see usePaymentPopup.js. Primary strategy when framed; buildCallbackUrl's
  // notification-url fallback only applies if the popup can't be opened.
  const relayUrl = useMemo(() => buildRelayUrl(config), [config]);
  const relayOrigin = useMemo(() => buildRelayOrigin(config), [config]);
  const callbackUrl = useMemo(
    () => buildCallbackUrl({}, config?.react_widget_notification_url),
    [config]
  );
  const navigateToPaymentStatus = useNavigateToPaymentStatus();
  const paymentPopup = usePaymentPopup(relayOrigin, () => {
    navigateToPaymentStatus(invoice?.id, selectedProcessor);
  });

  const { mutate: initPaymentQuery, isPending: initPaymentPending } = useInitPayment({
    invoice_id: invoice?.id,
    system: selectedProcessor,
    callback_url: callbackUrl,
    ...recurringPayment.recurringParams,
    ...payment.storeParams,
  });
  useEffect(() => {
    if (!isLoading && !processorsLoading) {
      clearParams();
    }
  }, [clearParams, isLoading, processorsLoading]);


  const initPayment = useCallback(() => {
    // When framed, open payment in a new top-level window - an in-place redirect can hit an
    // iframe sandbox that blocks the processor's top-navigation (e.g. Wix). Must be synchronous,
    // before the async call. Falls back to in-place navigation if the popup is blocked.
    const popup =
      isWidgetFramed() && relayUrl
        ? paymentPopup.open({ invoiceId: invoice?.id, system: selectedProcessor })
        : null;
    const usingPopup = !!popup;

    initPaymentQuery(
      { callback_url: usingPopup ? relayUrl : callbackUrl },
      {
        onSuccess: (data) => {
          if (data.redirect_url) {
            if (isSafeRedirectUrl(data.redirect_url)) {
              if (usingPopup) {
                paymentPopup.navigate(data.redirect_url);
              } else {
                window.location.href = data.redirect_url;
              }
            } else {
              if (usingPopup) paymentPopup.close();
              toast({
                title: "Error!",
                description: TOAST_ERROR_MESSAGE.DEFAULT,
                variant: "error",
              });
            }
          } else if (data.inline_content) {
            if (usingPopup) paymentPopup.close();
            toast({
              title: "Error!",
              description: TOAST_ERROR_MESSAGE.PAYMENT_METHOD_UNSUPPORTED,
              variant: "error",
            });
          } else if (usingPopup) {
            paymentPopup.close();
          }
        },
        onError: (error) => {
          if (usingPopup) paymentPopup.close();
          toast({
            title: "Error!",
            description: extractErrorMessage(error, TOAST_ERROR_MESSAGE.DEFAULT),
            variant: "error",
          });
        },
      }
    );
  }, [
    initPaymentQuery,
    invoice,
    selectedProcessor,
    callbackUrl,
    relayUrl,
    paymentPopup.open,
    paymentPopup.navigate,
    paymentPopup.close,
    toast,
  ]);

  const { selectedMethodId, payWithSavedMethod } = payment;
  const handleProceed = useCallback(() => {
    if (!selectedMethodId) {
      initPayment();
      return;
    }

    payWithSavedMethod((paidInvoice) => navigate(getInvoiceStatusRoute(invoice, activeFlow), {
      state: { invoiceId: paidInvoice.id },
    }));
  }, [selectedMethodId, payWithSavedMethod, initPayment, navigate, invoice, activeFlow]);

  if (isLoading || processorsLoading || payment.isLoadingSavedMethods) {
    return (
      <Container>
        <NavigationHeader
          title={
            invoice?.number ? (
              <>
                <T>offer #</T> {invoice.number}
              </>
            ) : (
              <SkeletonText size="h3" width="8rem" />
            )
          }
          back={backTarget}
        />
        <LoadingIndicator page />
      </Container>
    );
  }

  // The invoice list routes by status alone; `can_be_paid` also turns false once the payment
  // timeout releases the order. It does not cover a paid invoice awaiting confirmation.
  if (invoice && !invoice.can_be_paid && invoice.status !== INVOICE_STATUS.PAID) {
    return <Navigate replace to={ROUTES.INVOICE_DETAILS.replace(":id", invoice.id)} />;
  }

  return (
    <Container>
      <NavigationHeader
        title={
          <>
            <T>offer #</T> {invoice.number}
          </>
        }
        back={backTarget}
      />
      <Stack $gap="1rem">
        <InvoiceCard invoice={invoice} renderLineExtra={recurringPayment.renderLineExtra} />
        <RecurringChargesBlock invoice={invoice} optedIn={recurringPayment.autoRenew} />
      </Stack>
      {payment.showSavedMethods && (
        <StyledSavedPaymentMethodsList
          methods={payment.savedMethods}
          selectedId={payment.selectedMethodId}
          onSelect={payment.selectSavedMethod}
        />
      )}
      <StyledPaymentProcessorsList
        title="Choose payment method"
        subtitle="Please click on payment method to select it"
      >
        {filteredPaymentProcessors.map((processor) => (
          <PaymentProcessorItem
            key={processor.id}
            processor={processor}
            selected={selectedProcessor === processor.id}
            onClick={() => payment.selectProcessor(processor.id)}
          />
        ))}
      </StyledPaymentProcessorsList>
      {payment.canSaveMethod && (
        <SaveOptionWrap>
          <SavePaymentMethodOption checked={payment.saveMethod} onChange={payment.setSaveMethod} />
        </SaveOptionWrap>
      )}
      <ProceedButton
        variant="primary"
        disabled={!payment.hasSelection || paymentPopup.isPending}
        onClick={handleProceed}
        loading={initPaymentPending || payment.isPaying || paymentPopup.isPending}
        title="Proceed"
        fullWidth
      />
    </Container>
  );
};

export default InvoicePay;
